import React, { useState, useEffect } from "react";
import AddDistrictModal from "../../modals/AddDistrictModal";
import AddSchoolModal from "../../modals/AddSchoolModal";
import AddAdminModal from "../../modals/AddAdminModal";
import ConfirmDeleteDistrictModal from "../../modals/ConfirmDeleteDistrictModal";
import EditDistrictModal from "../../modals/EditDistrictModal";
import { DistrictItem } from "./DistrictItem";
import supabase from "../../../utils/supabase";
import { toast } from "react-toastify";
import { Plus } from "lucide-react";

interface District {
  id: string;
  name: string;
  user_id: string;
  created_at: string;
  state?: number | null;
}

interface Profile {
  id: string;
  fullname: string;
}

interface State {
  id: string;
  name: string;
}

export function Districts() {
  const [districts, setDistricts] = useState<District[]>([]);
  const [profiles, setProfiles] = useState<Record<string, Profile>>({});
  const [states, setStates] = useState<Record<string, State>>({});
  const [schoolCounts, setSchoolCounts] = useState<Record<string, number>>({});
  const [adminCounts, setAdminCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDistrictOpen, setIsAddDistrictOpen] = useState(false);
  const [isAddSchoolOpen, setIsAddSchoolOpen] = useState(false);
  const [isAddAdminOpen, setIsAddAdminOpen] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState<District | null>(
    null,
  );
  const [selectedAdminDistrict, setSelectedAdminDistrict] =
    useState<District | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [districtToDelete, setDistrictToDelete] = useState<District | null>(
    null,
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [districtToEdit, setDistrictToEdit] = useState<District | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  async function fetchDistricts(search?: string) {
    setIsLoading(true);
    try {
      let query = supabase
        .from("districts")
        .select("*, state(id, name)")
        .order("created_at", { ascending: false });

      if (search?.trim()) {
        query = query.ilike("name", `%${search.trim()}%`);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Failed to fetch districts:", error);
        return;
      }

      setDistricts(data || []);

      // Fetch profiles for all district creators and states
      if (data && data.length > 0) {
        const userIds = [...new Set(data.map((d) => d.user_id))];
        await fetchProfiles(userIds);
        await fetchStates(data);
        await fetchSchoolCounts(data.map((d) => d.id));
        await fetchAdminCounts(data.map((d) => d.id));
      }
    } catch (error) {
      console.error("Error fetching districts:", error);
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchProfiles(userIds: string[]) {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, fullname")
        .in("id", userIds);

      if (error) {
        console.error("Failed to fetch profiles:", error);
        return;
      }

      const profilesMap: Record<string, Profile> = {};
      data?.forEach((profile) => {
        profilesMap[profile.id] = profile;
      });
      setProfiles(profilesMap);
    } catch (error) {
      console.error("Error fetching profiles:", error);
    }
  }

  async function fetchStates(districtsData: any[]) {
    try {
      const statesMap: Record<string, State> = {};
      for (const district of districtsData || []) {
        // Handle both 'state' and 'states' relation names
        const stateData = (district as any).state || (district as any).states;
        if (stateData?.id) {
          statesMap[stateData.id] = stateData;
        }
      }
      setStates(statesMap);
    } catch (error) {
      console.error("Error processing states:", error);
    }
  }

  async function fetchSchoolCounts(districtIds: string[]) {
    try {
      const { data, error } = await supabase
        .from("schools")
        .select("district_id")
        .in(
          "district_id",
          districtIds.map((item) => Number(item)),
        );

      if (error) {
        console.error("Failed to fetch school counts:", error);
        return;
      }

      const counts: Record<string, number> = {};
      districtIds.forEach((id) => {
        counts[id] = 0;
      });

      data?.forEach((school) => {
        counts[school.district_id] = (counts[school.district_id] || 0) + 1;
      });

      setSchoolCounts(counts);
    } catch (error) {
      console.error("Error fetching school counts:", error);
    }
  }

  async function fetchAdminCounts(districtIds: string[]) {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("district, role")
        .in("district", districtIds)
        .eq("role", "admin");
      if (error) {
        console.error("Failed to fetch admin counts:", error);
        return;
      }
      const counts: Record<string, number> = {};
      districtIds.forEach((id) => {
        counts[id] = 0;
      });
      data?.forEach((profile) => {
        if (profile.district) {
          counts[profile.district] = (counts[profile.district] || 0) + 1;
        }
      });
      setAdminCounts(counts);
    } catch (error) {
      console.error("Error fetching admin counts:", error);
    }
  }

  async function updateSchoolCountForDistrict(districtId: string) {
    try {
      const { data, error } = await supabase
        .from("schools")
        .select("district_id")
        .eq("district_id", Number(districtId));

      if (error) {
        console.error("Failed to fetch school count for district:", error);
        return;
      }

      setSchoolCounts((prev) => ({
        ...prev,
        [districtId]: data?.length || 0,
      }));
    } catch (error) {
      console.error("Error updating school count for district:", error);
    }
  }

  async function updateAdminCountForDistrict(districtId: string) {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("district, role")
        .eq("district", districtId)
        .eq("role", "admin");
      if (error) {
        console.error("Failed to fetch admin count for district:", error);
        return;
      }
      setAdminCounts((prev) => ({
        ...prev,
        [districtId]: data?.length || 0,
      }));
    } catch (error) {
      console.error("Error updating admin count for district:", error);
    }
  }

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchDistricts(searchTerm);
      },
      searchTerm ? 500 : 0,
    ); // No delay for initial load, 500ms delay for search

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleDeleteClick = (district: District) => {
    setDistrictToDelete(district);
    setIsDeleteModalOpen(true);
  };

  const handleEditClick = (district: District) => {
    setDistrictToEdit(district);
    setIsEditModalOpen(true);
  };

  const handleDistrictDeleted = () => {
    // Refresh the districts list
    fetchDistricts(searchTerm);
  };

  const handleDistrictEdited = () => {
    // Refresh the districts list
    fetchDistricts(searchTerm);
  };

  const handleAddSchool = (district: District) => {
    setSelectedDistrict(district);
    setIsAddSchoolOpen(true);
  };

  const handleAddAdmin = (district: District) => {
    setSelectedAdminDistrict(district);
    setIsAddAdminOpen(true);
  };

  const handleSchoolAdded = (districtId: string) => {
    updateSchoolCountForDistrict(districtId);
  };

  const handleAdminAdded = (districtId: string) => {
    updateAdminCountForDistrict(districtId);
  };

  return (
    <div>
      {/* Modals */}
      <AddDistrictModal
        isOpen={isAddDistrictOpen}
        onClose={() => setIsAddDistrictOpen(false)}
        onSuccess={() => {
          setIsAddDistrictOpen(false);
          fetchDistricts(searchTerm);
        }}
      />

      <AddSchoolModal
        isOpen={isAddSchoolOpen}
        onClose={() => {
          setIsAddSchoolOpen(false);
          setSelectedDistrict(null);
        }}
        districtId={selectedDistrict?.id || ""}
        districtName={selectedDistrict?.name || ""}
        onSuccess={handleSchoolAdded}
      />

      <AddAdminModal
        isOpen={isAddAdminOpen}
        onClose={() => {
          setIsAddAdminOpen(false);
          setSelectedAdminDistrict(null);
        }}
        districtId={
          selectedAdminDistrict?.id ? Number(selectedAdminDistrict.id) : 0
        }
        districtName={selectedAdminDistrict?.name || ""}
        onSuccess={() => {
          setIsAddAdminOpen(false);
          setSelectedAdminDistrict(null);
          if (selectedAdminDistrict?.id)
            handleAdminAdded(selectedAdminDistrict.id);
        }}
      />

      <ConfirmDeleteDistrictModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDistrictToDelete(null);
        }}
        district={districtToDelete}
        onDistrictDeleted={handleDistrictDeleted}
      />

      <EditDistrictModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setDistrictToEdit(null);
        }}
        district={districtToEdit}
        onSuccess={handleDistrictEdited}
      />

      {/* Header */}
      <div className="bg-white border-b shadow-sm p-6 flex justify-between gap-5 items-start lg:items-center flex-col lg:flex-row">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Districts</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage districts across the platform.
          </p>
        </div>
        <div className="space-x-2 flex gap-2">
          <button
            onClick={() => setIsAddDistrictOpen(true)}
            className="btn btn-small"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add District
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto py-6 px-4 bg-gray-50">
        {/* Filters */}
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Districts
              </label>
              <input
                type="text"
                className="w-full p-2 border rounded-lg"
                placeholder="Search by district name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    District Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    State
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Schools
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Admins
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center text-sm text-gray-500 py-6 italic"
                    >
                      Loading districts...
                    </td>
                  </tr>
                ) : districts.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center text-sm text-gray-500 py-6"
                    >
                      {searchTerm
                        ? "No districts found matching your search."
                        : "No districts found. Add your first district to get started."}
                    </td>
                  </tr>
                ) : (
                  districts.map((district) => {
                    // Extract state name from relation data or lookup
                    const stateData =
                      (district as any).state || (district as any).states;
                    const stateName =
                      stateData?.name ||
                      (district.state && states[district.state]?.name) ||
                      "N/A";
                    return (
                      <DistrictItem
                        key={district.id}
                        district={district}
                        stateName={stateName}
                        creatorName={
                          profiles[district.user_id]?.fullname || "Unknown User"
                        }
                        schoolsCount={schoolCounts[district.id] || 0}
                        adminsCount={adminCounts[district.id] || 0}
                        onDelete={handleDeleteClick}
                        onEdit={handleEditClick}
                        onAddSchool={handleAddSchool}
                        onAddAdmin={handleAddAdmin}
                      />
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
